import { useMemo } from 'react';

import { useParams } from 'react-router-dom';

import { gql, useQuery, useMutation } from '@zenbusiness/application-commons-graphql';
import { useError } from '@zenbusiness/application-commons-logging';

import { BILLING_SUBSCRIPTION_CHANGE_MUTATION } from './useBillingSubscriptionChange';
import { useBillingSubscriptions } from './useBillingSubscriptions';

export const PAYMENT_METHODS_QUERY = gql`
  query paymentMethodsQuery {
    paymentMethods {
      defaultMethodId
      methods {
        id
        brand
        last4
      }
    }
  }
`;

/**
 * Data and actions for the subscription restart flow. Uses shared billing queries/mutation.
 * @returns {{ subscription: object|null, paymentMethod: object|null, loading: boolean, hasError: boolean, submitChange: Function, submitting: boolean }}
 */
export function useSubscriptionRestart() {
  const { orderBillingItemUuid } = useParams();

  const { data: activeSubscriptions, loading, hasError } = useBillingSubscriptions();

  const { data: paymentData, loading: paymentLoading, error: paymentError } = useQuery(
    PAYMENT_METHODS_QUERY,
    { noThrow: true }
  );

  useError(paymentError);

  const [runChange, { loading: submitting, error: mutationError }] = useMutation(
    BILLING_SUBSCRIPTION_CHANGE_MUTATION,
    { noThrow: true }
  );

  useError(mutationError);

  const subscription = useMemo(
    () => activeSubscriptions.find((s) => s.orderBillingItemUuid === orderBillingItemUuid) ?? null,
    [activeSubscriptions, orderBillingItemUuid]
  );

  const paymentMethod = useMemo(() => {
    const methods = paymentData?.paymentMethods?.methods || [];
    const defaultId = paymentData?.paymentMethods?.defaultMethodId;
    return methods.find((m) => m.id === defaultId) || methods[0] || null;
  }, [paymentData]);

  return {
    subscription,
    paymentMethod,
    loading: loading || paymentLoading,
    hasError: hasError || Boolean(paymentError),
    submitChange: runChange,
    submitting
  };
}
